import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Copy, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" /></svg>
);
const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M9 3C4.6 3 1 6 1 9.7c0 2.1 1.2 4 3 5.2l-.8 2.4 2.8-1.5c1 .3 2 .4 3 .4h.6a5.6 5.6 0 0 1-.2-1.5c0-3.4 3.3-6.2 7.4-6.2h.6C16.7 5.4 13.2 3 9 3zM6 7.2a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm5.9 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM16.6 10c-3.5 0-6.4 2.4-6.4 5.3s2.9 5.3 6.4 5.3c.7 0 1.5-.1 2.2-.3l2.3 1.2-.6-2c1.5-1 2.5-2.5 2.5-4.2 0-2.9-2.9-5.3-6.4-5.3zm-2.1 2.6a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8zm4.2 0a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z" /></svg>
);

export default function ShareDocDialog({ open, onOpenChange, kind, docId, docLabel, defaultPhone = '' }) {
  const [url, setUrl] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (!open || !docId) return;
    setUrl('');
    setPhone(defaultPhone || '');
    axios.post(`${API}/api/share-links`, { kind, doc_id: docId })
      .then((r) => setUrl(`${API}/api/public/share/${r.data.token}`))
      .catch(() => toast.error('Impossible de préparer le lien du document'));
  }, [open, docId, kind, defaultPhone]);

  const message = `Bonjour,\n\nVoici ${docLabel} de SESAM :\n${url}\n\nCordialement,\nSESAM`;

  const sendWhatsapp = () => {
    const digits = (phone || '').replace(/[^\d]/g, '');
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    toast.success('WhatsApp ouvert — choisissez le contact et envoyez');
    onOpenChange(false);
  };

  const sendWechat = async () => {
    try { await navigator.clipboard.writeText(message); } catch { /* clipboard indisponible */ }
    toast.success('Message copié — collez-le dans la conversation WeChat');
    window.location.href = 'weixin://';
    onOpenChange(false);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('Lien copié'); } catch { toast.error('Copie impossible'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden" data-testid="share-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-600" /> Envoyer {docLabel}</DialogTitle>
          <DialogDescription>Choisissez l'application. Le message contient le lien du PDF.</DialogDescription>
        </DialogHeader>
        <div className="min-w-0">
          <Label className="text-xs text-slate-500">Numéro WhatsApp (facultatif)</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+236 75 00 00 00" data-testid="share-phone-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={sendWhatsapp} disabled={!url}
            className="flex flex-col items-center gap-2 rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5 text-emerald-700 font-bold transition-transform hover:-translate-y-0.5 hover:border-emerald-400 disabled:opacity-50"
            data-testid="share-whatsapp-btn"
          >
            <WhatsAppIcon /> WhatsApp
          </button>
          <button
            onClick={sendWechat} disabled={!url}
            className="flex flex-col items-center gap-2 rounded-xl border-2 border-green-200 bg-green-50 p-5 text-green-700 font-bold transition-transform hover:-translate-y-0.5 hover:border-green-400 disabled:opacity-50"
            data-testid="share-wechat-btn"
          >
            <WeChatIcon /> WeChat
          </button>
        </div>
        <div className="min-w-0 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
          {url ? <span className="truncate flex-1 min-w-0" data-testid="share-link-url">{url}</span> : <span className="flex-1 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Préparation du lien…</span>}
          <button onClick={copyLink} disabled={!url} className="text-blue-600 hover:text-blue-800" title="Copier le lien" data-testid="share-copy-link-btn"><Copy className="h-4 w-4" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
